import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type {
  BulkUsersInput,
  BulkUsersResponse,
  ChangeDelegationDatesInput,
  CheckDelegationInput,
  CreateDelegationInput,
  DeactivateUserInput,
  DelegationCheckResult,
  DelegationView,
  InviteManyInput,
  InviteManyResponse,
  RoleKey,
  RoleOwnership,
  RoleScopeKind,
  SetAvailabilityInput,
  SetUserRolesInput,
  SuspendUserInput,
  UpdateUserInput,
  UserListFilter,
  UserStatus,
  UserStatusCounts,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost, request } from '../../../lib/api-client';

export interface UserListRow {
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly status: UserStatus;
  readonly lastLoginAt: string | null;
  readonly isPanelEligible: boolean;
  readonly isTwoFactorEnabled: boolean;
  readonly lockedUntil: string | null;
  /** Wrong passwords since the last good sign-in: "5 wrong passwords" on a locked person. */
  readonly failedLoginCount: number;
  readonly roleLabels: string;
  readonly ventureCodes: string | null;
  /** "Whole organisation" only for an organisation-wide role, else the venture codes and department names; null for no role (review R15). */
  readonly reachLabel: string | null;
  /** Invited people only: the invite Resend acts on, and when it runs out. */
  readonly pendingInviteId: string | null;
  readonly inviteExpiresAt: string | null;
}

export interface UserAssignment {
  readonly id: string;
  readonly roleId: string;
  readonly roleLabel: string;
  readonly roleKey: RoleKey | null;
  readonly scopeKind: RoleScopeKind;
  readonly ownership: RoleOwnership;
  readonly ventureId: string | null;
  readonly ventureName: string | null;
  readonly departmentId: string | null;
  readonly departmentName: string | null;
}

export interface AvailabilityRow {
  readonly id: string;
  readonly dayOfWeek: number | null;
  readonly startTime: string | null;
  readonly endTime: string | null;
  readonly blockedFrom: string | null;
  readonly blockedTo: string | null;
  readonly reason: string | null;
}

export interface UserSessionRow {
  readonly id: string;
  readonly ipAddress: string | null;
  readonly userAgent: string | null;
  readonly lastSeenAt: string;
  readonly expiresAt: string;
  readonly isImpersonating: boolean;
}

export interface OwnedRecordCounts {
  readonly requisitions: number;
  readonly jobs: number;
  /** Bug #53: open jobs they are line manager on. */
  readonly lineManagedJobs: number;
  readonly tasks: number;
  readonly total: number;
}

export interface UserDetail {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly displayName: string | null;
  readonly phone: string | null;
  readonly status: UserStatus;
  readonly timezone: string;
  readonly isPanelEligible: boolean;
  readonly isTwoFactorEnabled: boolean;
  readonly managerUserId: string | null;
  readonly managerName: string | null;
  readonly lastLoginAt: string | null;
  readonly lockedUntil: string | null;
  readonly failedLoginCount: number;
  readonly isGuest: boolean;
  /** D14 / S6: the personal work-calendar override. Null means they inherit. */
  readonly workCalendarId: string | null;
  readonly version: number;
  readonly assignments: readonly UserAssignment[];
  readonly availability: readonly AvailabilityRow[];
  readonly ownedRecords: OwnedRecordCounts;
  readonly sessions: readonly UserSessionRow[];
  /** Invited people only: the invite waiting to be accepted. */
  readonly pendingInvite: UserPendingInvite | null;
}

export interface UserPendingInvite {
  readonly id: string;
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly invitedByName: string | null;
}

export interface PendingInvite {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly roleLabel: string;
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly invitedByName: string | null;
}

export interface Venture {
  readonly id: string;
  readonly name: string;
  readonly code: string;
}

export interface Department {
  readonly id: string;
  readonly name: string;
  readonly code: string | null;
  readonly ventureId: string;
}

export const USERS_KEY = ['users'] as const;
export const USER_COUNTS_KEY = ['users', 'counts'] as const;
export const INVITES_KEY = ['users', 'invites'] as const;
export const DELEGATIONS_KEY = ['users', 'delegations'] as const;
export const COLLEAGUES_KEY = ['users', 'colleagues'] as const;

/** Anyone signed in may read this: the people who could cover for them (bug #49). */
export interface Colleague {
  readonly id: string;
  readonly fullName: string;
}

/** USER-01's query: the filters, the page and the sort (`column:direction,…`, see `parseUserListSort`). */
export interface UserListParams extends UserListFilter {
  readonly page: number;
  readonly pageSize: number;
  readonly sort?: string;
}

export interface UserListPage {
  readonly rows: readonly UserListRow[];
  readonly total: number;
  /** The page these rows are, so a screen can tell the page it asked for from the one still on screen. */
  readonly page: number;
}

export function useUsers(params: UserListParams, isEnabled = true) {
  return useQuery<UserListPage, ApiError>({
    queryKey: [...USERS_KEY, 'list', params],
    enabled: isEnabled,
    queryFn: async () => {
      const result = await request<readonly UserListRow[]>('/users', { query: { ...params } });
      return { rows: result.data, total: result.meta?.total ?? result.data.length, page: params.page };
    },
    // Never blank a list the viewer can already see (CLAUDE.md §5a rule 2) — a
    // filter change swaps rows in place rather than dropping to a skeleton.
    placeholderData: (previous) => previous,
  });
}

/** USER-01's status tiles: one count per status, for everything the other filters let through. */
export function useUserCounts(filter: Omit<UserListFilter, 'status'>, isEnabled = true) {
  return useQuery<UserStatusCounts, ApiError>({
    queryKey: [...USER_COUNTS_KEY, filter],
    enabled: isEnabled,
    queryFn: () => apiGet<UserStatusCounts>('/users/counts', { query: { ...filter } }),
    placeholderData: (previous) => previous,
  });
}

export function useUser(id: string | null): UseQueryResult<UserDetail | null, ApiError> {
  return useQuery<UserDetail | null, ApiError>({
    queryKey: [...USERS_KEY, 'detail', id],
    queryFn: () => (id ? apiGet<UserDetail>(`/users/${id}`) : Promise.resolve(null)),
    enabled: id !== null,
  });
}

export function usePendingInvites() {
  return useQuery<readonly PendingInvite[], ApiError>({
    queryKey: INVITES_KEY,
    queryFn: () => apiGet<readonly PendingInvite[]>('/users/invites'),
  });
}

export function useVentures() {
  return useQuery<readonly Venture[], ApiError>({
    queryKey: ['organisation', 'ventures'],
    queryFn: () => apiGet<readonly Venture[]>('/organisation/ventures'),
    staleTime: 5 * 60_000,
  });
}

export function useDepartments() {
  return useQuery<readonly Department[], ApiError>({
    queryKey: ['organisation', 'departments'],
    queryFn: () => apiGet<readonly Department[]>('/organisation/departments'),
    staleTime: 5 * 60_000,
  });
}

/** USER-03: several addresses, one role and reach for all; each address reports what happened to it. */
export function useInviteMany() {
  const queryClient = useQueryClient();
  return useMutation<InviteManyResponse, ApiError, InviteManyInput>({
    mutationFn: (input) => apiPost<InviteManyResponse>('/users/invites/many', input),
    onSettled: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

/** USER-01's bulk actions. Each person is reported changed, skipped (with the rule's words) or failed. */
export function useBulkUsers() {
  const queryClient = useQueryClient();
  return useMutation<BulkUsersResponse, ApiError, BulkUsersInput>({
    mutationFn: (input) => apiPost<BulkUsersResponse>('/users/bulk', input),
    onSettled: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();
  return useMutation<{ inviteUrl: string }, ApiError, string>({
    mutationFn: (id) => apiPost<{ inviteUrl: string }>(`/users/invites/${id}/resend`),
    // The person's row and pane carry the invite's end date, so both refresh with the list of invites.
    onSettled: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiDelete<void>(`/users/invites/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/**
 * A change to one person's detail, shown before the server answers (06-qol §4): the pane changes at once, goes
 * back if the server refuses, and the list catches up once the refetch lands.
 *
 * Review R29: a refusal puts back only the fields this change previewed, and only where they still show its value,
 * so a newer edit made meanwhile (the name typed while the panel switch was saving) is never undone with it.
 */
function useDetailMutation<TInput>(
  id: string,
  send: (input: TInput) => Promise<UserDetail>,
  preview?: (current: UserDetail, input: TInput) => UserDetail,
) {
  const queryClient = useQueryClient();
  const key = [...USERS_KEY, 'detail', id];
  return useMutation<UserDetail, ApiError, TInput, DetailPreview | undefined>({
    mutationFn: send,
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<UserDetail>(key);
      if (!previous || !preview) return undefined;
      const next = preview(previous, input);
      queryClient.setQueryData(key, next);
      return previewOf(previous, next);
    },
    onError: (_error, _input, context) => {
      if (!context) return;
      queryClient.setQueryData<UserDetail>(key, (current) => (current ? undoPreview(current, context) : current));
    },
    onSuccess: (updated) => queryClient.setQueryData(key, updated),
    onSettled: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

/** What one optimistic change set: each field it changed, before and after. */
export interface DetailPreview {
  readonly before: Partial<UserDetail>;
  readonly after: Partial<UserDetail>;
}

export function previewOf(previous: UserDetail, next: UserDetail): DetailPreview {
  const before: Partial<UserDetail> = {};
  const after: Partial<UserDetail> = {};
  for (const field of Object.keys(next) as (keyof UserDetail)[]) {
    if (Object.is(previous[field], next[field])) continue;
    Object.assign(before, { [field]: previous[field] });
    Object.assign(after, { [field]: next[field] });
  }
  return { before, after };
}

/** Puts back what the change set, field by field, leaving any field that has moved on since as it is. */
export function undoPreview(current: UserDetail, preview: DetailPreview): UserDetail {
  const restore: Partial<UserDetail> = {};
  for (const field of Object.keys(preview.after) as (keyof UserDetail)[]) {
    if (Object.is(current[field], preview.after[field])) Object.assign(restore, { [field]: preview.before[field] });
  }
  return { ...current, ...restore };
}

export function useUpdateUser(id: string) {
  return useDetailMutation<UpdateUserInput>(
    id,
    (input) => apiPatch<UserDetail>(`/users/${id}`, input),
    (current, input) => ({
      ...current,
      ...(input.fullName === undefined ? {} : { fullName: input.fullName }),
      ...(input.isPanelEligible === undefined ? {} : { isPanelEligible: input.isPanelEligible }),
    }),
  );
}

export function useSetUserRoles(id: string) {
  const queryClient = useQueryClient();
  return useMutation<UserDetail, ApiError, SetUserRolesInput>({
    mutationFn: (input) =>
      request<UserDetail>(`/users/${id}/roles`, { method: 'PUT', body: input }).then((r) => r.data),
    onSuccess: (updated) => {
      queryClient.setQueryData([...USERS_KEY, 'detail', id], updated);
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/** Not shown ahead of the server: the hand-over may be refused (`reassignment_required`), and nothing moves until it is not. */
export function useDeactivateUser(id: string) {
  return useDetailMutation<DeactivateUserInput>(id, (input) => apiPost<UserDetail>(`/users/${id}/deactivate`, input));
}

export function useReactivateUser(id: string) {
  return useDetailMutation<void>(
    id,
    () => apiPost<UserDetail>(`/users/${id}/reactivate`),
    (current) => ({ ...current, status: 'active' }),
  );
}

/** Blocks sign-in and ends their sessions; moves nothing. The body is at least `{}`. */
export function useSuspendUser(id: string) {
  return useDetailMutation<SuspendUserInput>(
    id,
    (input) => apiPost<UserDetail>(`/users/${id}/suspend`, input),
    (current) => ({ ...current, status: 'suspended', sessions: [] }),
  );
}

export function useUnsuspendUser(id: string) {
  return useDetailMutation<void>(
    id,
    () => apiPost<UserDetail>(`/users/${id}/unsuspend`),
    (current) => ({ ...current, status: 'active' }),
  );
}

/** Emails them the self-service reset link. The answer names the address it went to; Admin never sees a password. */
export function useSendPasswordReset(id: string) {
  return useMutation<{ email: string }, ApiError, void>({
    mutationFn: () => apiPost<{ email: string }>(`/users/${id}/reset-password`),
  });
}

export function useUnlockUser(id: string) {
  const queryClient = useQueryClient();
  const key = [...USERS_KEY, 'detail', id];
  return useMutation<void, ApiError, void, { previous: UserDetail | undefined }>({
    mutationFn: () => apiPost<void>(`/users/${id}/unlock`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<UserDetail>(key);
      if (previous) queryClient.setQueryData(key, { ...previous, lockedUntil: null, failedLoginCount: 0 });
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useRevokeUserSession(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (sessionId) => apiDelete<void>(`/users/${userId}/sessions/${sessionId}`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [...USERS_KEY, 'detail', userId] }),
  });
}

export function useSetAvailability(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<readonly AvailabilityRow[], ApiError, SetAvailabilityInput>({
    mutationFn: (input) =>
      request<readonly AvailabilityRow[]>(`/users/${userId}/availability`, {
        method: 'PUT',
        body: input,
      }).then((r) => r.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...USERS_KEY, 'detail', userId] });
    },
  });
}

// --- F2.11 delegation --------------------------------------------------------
//
// Instant feel (ADR 0002): every change to a cover shows in the list at once, is rolled back if the server refuses it, and is held
// until the refetch lands. Both lists — yours, and an Admin's "Everyone" — are changed, so whichever is on screen moves.

/** The dry run asks after the person stops changing the form, not on every click of a date (06-qol §6: 250ms). */
const COVER_CHECK_DEBOUNCE_MS = 250;

type DelegationSnapshot = readonly (readonly [QueryKey, readonly DelegationView[] | undefined])[];

async function changeDelegationLists(
  queryClient: QueryClient,
  change: (rows: readonly DelegationView[], isEveryone: boolean) => readonly DelegationView[],
): Promise<DelegationSnapshot> {
  await queryClient.cancelQueries({ queryKey: DELEGATIONS_KEY });
  const snapshot = queryClient.getQueriesData<readonly DelegationView[]>({ queryKey: DELEGATIONS_KEY });
  for (const [key, rows] of snapshot) {
    if (rows) queryClient.setQueryData(key, change(rows, key[2] === true));
  }
  return snapshot;
}

function restoreDelegationLists(queryClient: QueryClient, snapshot: DelegationSnapshot | undefined): void {
  for (const [key, rows] of snapshot ?? []) queryClient.setQueryData(key, rows);
}

export function useDelegations(includeEveryone = false) {
  return useQuery<readonly DelegationView[], ApiError>({
    queryKey: [...DELEGATIONS_KEY, includeEveryone],
    queryFn: () =>
      apiGet<readonly DelegationView[]>('/users/delegations', {
        query: includeEveryone ? { all: 'true' } : {},
      }),
    // Switching between "Everyone" and "Only mine" swaps the rows in place (06-qol §5).
    placeholderData: (previous) => previous,
  });
}

export function useColleagues() {
  return useQuery<readonly Colleague[], ApiError>({
    queryKey: COLLEAGUES_KEY,
    queryFn: () => apiGet<readonly Colleague[]>('/users/colleagues'),
  });
}

export interface CreateDelegationVariables {
  readonly input: CreateDelegationInput;
  /** The row as it will look, shown before the server answers. */
  readonly preview: DelegationView;
}

/** `viewerId`: a cover for two other people goes only into an Admin's "Everyone" list, not into "Only mine". */
export function useCreateDelegation(viewerId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<readonly DelegationView[], ApiError, CreateDelegationVariables, DelegationSnapshot>({
    mutationFn: ({ input }) => apiPost<readonly DelegationView[]>('/users/delegations', input),
    onMutate: ({ preview }) =>
      changeDelegationLists(queryClient, (rows, isEveryone) =>
        isEveryone || preview.fromUserId === viewerId || preview.toUserId === viewerId ? [preview, ...rows] : rows,
      ),
    onError: (_error, _variables, snapshot) => restoreDelegationLists(queryClient, snapshot),
    onSettled: () => queryClient.invalidateQueries({ queryKey: DELEGATIONS_KEY }),
  });
}

/** "End now" for a cover in force, "Cancel" for one still to come: the row ends at once. */
export function useEndDelegation() {
  const queryClient = useQueryClient();
  return useMutation<readonly DelegationView[], ApiError, string, DelegationSnapshot>({
    mutationFn: (id) => apiDelete<readonly DelegationView[]>(`/users/delegations/${id}`),
    onMutate: (id) => {
      const revokedAt = new Date().toISOString();
      return changeDelegationLists(queryClient, (rows) =>
        rows.map((row) => (row.id === id ? { ...row, revokedAt, waitingTooLong: 0 } : row)),
      );
    },
    onError: (_error, _id, snapshot) => restoreDelegationLists(queryClient, snapshot),
    onSettled: () => queryClient.invalidateQueries({ queryKey: DELEGATIONS_KEY }),
  });
}

/**
 * Undo after "End now" or "Cancel" (review R6): the same cover comes back on the same row, so nothing is counted twice and the stand-in
 * is not told again. The server allows it only shortly after the cover ended (`COVER_RESTORE_WINDOW_SECONDS`).
 */
export function useRestoreDelegation() {
  const queryClient = useQueryClient();
  return useMutation<readonly DelegationView[], ApiError, string, DelegationSnapshot>({
    mutationFn: (id) => apiPost<readonly DelegationView[]>(`/users/delegations/${id}/restore`, {}),
    onMutate: (id) => changeDelegationLists(queryClient, (rows) => rows.map((row) => (row.id === id ? { ...row, revokedAt: null } : row))),
    onError: (_error, _id, snapshot) => restoreDelegationLists(queryClient, snapshot),
    onSettled: () => queryClient.invalidateQueries({ queryKey: DELEGATIONS_KEY }),
  });
}

export interface ChangeDelegationDatesVariables {
  readonly id: string;
  readonly input: ChangeDelegationDatesInput;
}

/** "Change dates": `version` makes a stale write a 409, which rolls the row back with the server's words. */
export function useChangeDelegationDates() {
  const queryClient = useQueryClient();
  return useMutation<readonly DelegationView[], ApiError, ChangeDelegationDatesVariables, DelegationSnapshot>({
    mutationFn: ({ id, input }) => apiPatch<readonly DelegationView[]>(`/users/delegations/${id}`, input),
    onMutate: ({ id, input }) =>
      changeDelegationLists(queryClient, (rows) =>
        rows.map((row) => (row.id === id ? { ...row, startsAt: input.startsAt, endsAt: input.endsAt } : row)),
      ),
    onError: (_error, _variables, snapshot) => restoreDelegationLists(queryClient, snapshot),
    onSettled: () => queryClient.invalidateQueries({ queryKey: DELEGATIONS_KEY }),
  });
}

export interface DelegationCheck {
  /** Why the cover can't be saved, in the server's words; null when it can, or while the answer is not in. */
  readonly problem: string | null;
  readonly isChecking: boolean;
}

/**
 * The dry run before Save (`POST /users/delegations/check`): the same rules as saving, saving nothing, so the form can warn that the
 * stand-in is away too before anyone presses the button. `null` asks nothing (the form is not filled in yet).
 */
export function useDelegationCheck(input: CheckDelegationInput | null): DelegationCheck {
  const wanted = input === null ? null : JSON.stringify(input);
  const [settled, setSettled] = useState(wanted);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(wanted), COVER_CHECK_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [wanted]);

  // Never answer for dates the form no longer shows: until the settled input matches, nothing is said.
  const isSettled = settled === wanted;
  const check = useQuery<DelegationCheckResult, ApiError>({
    queryKey: ['users', 'delegation-check', settled],
    queryFn: () => apiPost<DelegationCheckResult>('/users/delegations/check', JSON.parse(settled ?? '{}') as CheckDelegationInput),
    enabled: settled !== null && isSettled,
    retry: false,
  });

  if (wanted === null) return { problem: null, isChecking: false };
  if (!isSettled) return { problem: null, isChecking: true };
  if (check.data) return { problem: check.data.problem, isChecking: false };
  if (check.error) return { problem: check.error.message, isChecking: false };
  return { problem: null, isChecking: true };
}
